import { MiraAvailabilityState, MiraPublishedMetric, MiraPublishedPeriod } from "@/data/models/Mira";
import {
  COLOR_MIRA_AREA_FLOOR,
  COLOR_MIRA_AREA_FLOOR_DARK,
  COLOR_MIRA_AREA_WALL,
  COLOR_MIRA_AREA_WALL_DARK,
  COLOR_MIRA_BAR,
  COLOR_MIRA_BAR_DARK,
  COLOR_MIRA_BAR_TRACK,
  COLOR_MIRA_BAR_TRACK_DARK,
  COLOR_MIRA_FAMILY_BOX,
  COLOR_MIRA_FAMILY_BOX_DARK,
  COLOR_MIRA_FAMILY_MINI,
  COLOR_MIRA_FAMILY_MINI_DARK,
  COLOR_MIRA_FAMILY_PANEL,
  COLOR_MIRA_FAMILY_PANEL_DARK,
  COLOR_MIRA_PAUSE_DRYING,
  COLOR_MIRA_PAUSE_DRYING_DARK,
  COLOR_MIRA_PAUSE_POWER_CUT,
  COLOR_MIRA_PAUSE_POWER_CUT_DARK,
} from "@/utils/colors";
import {
  MiraBar,
  MiraSegment,
  MiraTile,
  MiraViewContext,
  MiraViewDelta,
  deltaPercentText,
  formatPercent,
  medianMeasure,
  metricTitle,
  publishedMetric,
  spreadLine,
} from "@/utils/MiraDataQualityView";
import { MiraValueFormatter } from "@/utils/MiraTrendChart";
import { belowRowFloor, casesText, unitText } from "@/utils/MiraCaseUnits";

/**
 * The Trocknung screen's view model: what each tile, dot plot row, split and fact shows, built from a
 * published period and its comparison period. Pure, like the Datenqualität view model it builds on.
 *
 * Three decisions shape it (Taimoor, 2026-09-30), and each is pinned by a test:
 * - **No HyDry-versus-conventional comparison.** The HyDry benefit figure (DRY-004) is held back until
 *   a rule for which dryings are comparable exists, so nothing here sets the two drying times side by
 *   side, and forecast versus actual is shown overall, without its published HyDry / conventional split.
 * - **Changes are neutral**: a shorter drying is not automatically a better one.
 * - **The mean leads, the median beside it**, as the figures are published.
 *
 * Two views of a drying (Hans, Track #2610040 O1 / O2): per drying, over the HyDry dryings WITHOUT a project
 * (DRY-001, DRY-002), and per finished project (DRY-009 HyDry, DRY-010 konventionell, DRY-011 the HyDry
 * projects' pause share), so a project's drying is counted once however many chains it ran. DRY-010 replaces
 * DRY-003, which IRES Cloud still publishes and which no longer gets a tile (Taimoor, 2026-10-13).
 *
 * It never shows how many drying ends were reconstructed: those are Panel clock corrections, which
 * stay invisible to customers (`docs/devices/panel-clock-corrections.md`).
 */

/** The HyDry device categories, as the breakdowns key them, in display order. */
export const MIRA_FAMILIES = ["box", "mini", "panel"] as const;
export type MiraFamilyKey = typeof MIRA_FAMILIES[number];

/** The published family id (a caveat parameter) to its breakdown key. */
const FAMILY_BY_ID: Record<string, MiraFamilyKey> = { HYDRY_BOX: "box", HYDRY_MINI: "mini", HYDRY_PANEL: "panel" };

const FAMILY_COLOR: Record<MiraFamilyKey, string> = {
  box: COLOR_MIRA_FAMILY_BOX,
  mini: COLOR_MIRA_FAMILY_MINI,
  panel: COLOR_MIRA_FAMILY_PANEL,
};
const FAMILY_COLOR_DARK: Record<MiraFamilyKey, string> = {
  box: COLOR_MIRA_FAMILY_BOX_DARK,
  mini: COLOR_MIRA_FAMILY_MINI_DARK,
  panel: COLOR_MIRA_FAMILY_PANEL_DARK,
};

/** What the Trocknung view model needs on top of the shared context: decimals. */
export interface MiraDryingContext extends MiraViewContext {
  formatDecimal: (value: number, digits: number) => string;
}

/** One HyDry category's row in the dot plot: its mean and median drying time on a shared axis. */
export interface MiraDotRow {
  /** Unique within one plot: a device type on Trocknung, a phase or a wait on Prozess. */
  key: string;
  label: string;
  color: string;
  mean: number;
  median: number;
  meanText: string;
  medianText: string;
  countText: string;
  /**
   * The 15th and 85th percentiles, for the faint range behind the marks. Both present or neither;
   * drawn only when the screen allows percentiles (`VIEW_MIRA_PERCENTILES`).
   */
  p15?: number | null;
  p85?: number | null;
  /** Shown instead of the marks, e.g. "zu wenige Projekte"; the mean and median are then not drawn. */
  note?: string | null;
}

/** One HyDry category's pause share, split into its parts, as shares of its drying time. */
export interface MiraSplitRow {
  key: MiraFamilyKey;
  label: string;
  parts: Array<{ key: string; value: number; color: string }>;
  valueText: string;
  note: string;
}

/** A labelled fact under a figure: "Median", "Ohne Prognose", and the like. */
export interface MiraFact {
  label: string;
  value: string;
}

export function familyColor(family: MiraFamilyKey, dark: boolean): string {
  return dark ? FAMILY_COLOR_DARK[family] : FAMILY_COLOR[family];
}

export function familyLabel(family: MiraFamilyKey, ctx: MiraViewContext): string {
  return ctx.t(`mira.drying.families.${family}`);
}

/**
 * The category a family-split figure leads with, from its `headlineHydryFamily` caveat.
 *
 * A group's pooled figure (a group master, IRES's Alle Mandanten) carries no such caveat: IRES Cloud
 * drops it there on purpose, since the headline is evident from the breakdown. So without one the
 * category with the most dryings in the breakdown leads, the first in {@link MIRA_FAMILIES} on a tie,
 * the rule IRES Cloud picks it by. Without that, the Trocknungsdauer caption read "()" (#2610137).
 */
export function headlineFamily(metric: MiraPublishedMetric | null): MiraFamilyKey | null {
  const id = metric?.caveat?.key === "headlineHydryFamily" ? String(metric.caveat.params?.[0] ?? "") : "";
  if (FAMILY_BY_ID[id]) {
    return FAMILY_BY_ID[id];
  }

  let headline: MiraFamilyKey | null = null;
  let most = 0;
  for (const family of MIRA_FAMILIES) {
    const dryings = metric?.breakdown?.[`${family}Dryings`] ?? 0;
    if (dryings > most) {
      headline = family;
      most = dryings;
    }
  }
  return headline;
}

/**
 * A number of days as text, with the unit in the right number: "1 Tag", "+1 Tag", "−1 Tag", but
 * "1,5 Tage", "0 Tage", "2 Tage" (and "1,0 Tage", which the formatter never produces). Every Mira day
 * value goes through here, so the singular is never missed in one place.
 *
 * @param {string} magnitude - the formatted number, sign included ("+1", "−1,2", "±0").
 * @param {MiraViewContext} ctx - translation.
 * @returns {string} the number with its unit.
 */
export function daysText(magnitude: string, ctx: MiraViewContext): string {
  const one = /^[+\u2212±-]?1$/.test(magnitude.trim());
  return ctx.t(one ? "mira.drying.units.day" : "mira.drying.units.days", [magnitude]);
}

export function formatDays(days: number, ctx: MiraDryingContext): string {
  return daysText(ctx.formatDecimal(days, 1), ctx);
}

export function formatSquareMetres(area: number, ctx: MiraDryingContext): string {
  return ctx.t("mira.drying.units.squareMetres", [ctx.formatDecimal(area, 1)]);
}

export function formatPerWeek(rate: number, ctx: MiraDryingContext): string {
  return ctx.t("mira.drying.units.perWeek", [ctx.formatDecimal(rate, 2)]);
}

/** Intermediate measurements per project, two decimals: "0,52 pro Projekt" (DRY-012, #2610112 X5). */
export function formatPerProject(rate: number, ctx: MiraDryingContext): string {
  return ctx.t("mira.drying.units.perProject", [ctx.formatDecimal(rate, 2)]);
}

/** Zwischenmessungen je Projekt (#2610112 X5), which replaced Messfrequenz per week (DRY-005). */
export const MIRA_MEASUREMENTS_PER_PROJECT = "MIRA-DRY-012";

/**
 * Whether the period's document carries Zwischenmessungen je Projekt (any state), so the per-week figure
 * (DRY-005) is not drawn. A document from before IRES Cloud published it still shows the per-week figure.
 */
export function hasMeasurementsPerProject(period: MiraPublishedPeriod | null): boolean {
  return period?.metrics[MIRA_MEASUREMENTS_PER_PROJECT] !== undefined;
}

/**
 * A neutral change, in the figure's own unit: "+1,2 Tage", "−3 Pp.", "±0,00 pro Woche".
 *
 * Rounded to what the figure shows before the direction is read, so a change too small to see reads
 * as unchanged rather than as a rise of "+0,0". With `relative`, a visible change also carries its share
 * of the previous value ("(7 %)", `deltaPercentText`); a change in percentage points passes `false`.
 */
export function neutralDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
  digits: number,
  format: (magnitude: string) => string,
  ctx: MiraDryingContext,
  relative = true
): MiraViewDelta | null {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null;
  }

  const factor = 10 ** digits;
  const change = Math.round((current - previous) * factor) / factor || 0;
  const direction = change > 0 ? "up" : change < 0 ? "down" : "flat";
  const sign = direction === "up" ? "+" : direction === "down" ? "−" : "±";
  const delta: MiraViewDelta = { text: format(`${sign}${ctx.formatDecimal(Math.abs(change), digits)}`), direction, tone: "neutral" };

  // The relative change beside it (#2610137), except for a change in percentage points.
  const percentText = relative && direction !== "flat" ? deltaPercentText(current, previous, ctx) : undefined;
  return percentText ? { ...delta, percentText } : delta;
}

/** The unit a duration figure reads in. One per figure: its tile, caption, spread, delta and charts all share it. */
export type MiraDurationUnit = "days" | "hours";

/** One hour, in days: the step an hours chart axis counts in. */
export const MIRA_HOUR_IN_DAYS = 1 / 24;

/**
 * The unit a duration reads in: hours where it is under one day (and not zero), so 40 minutes does not
 * show as "0 Tage", else days. A duration figure picks it once, from its headline (the mean of the chosen
 * period), and then shows everything in it (Taimoor, 2026-10-07): a tile reading "8,2 Std." must not say
 * "Median 0 Tage" beside a chart in days.
 */
export function durationUnit(days: number | null | undefined): MiraDurationUnit {
  return days !== null && days !== undefined && days !== 0 && Math.abs(days) < 1 ? "hours" : "days";
}

function hoursText(magnitude: string, ctx: MiraViewContext): string {
  return ctx.t("mira.format.hours", [magnitude]);
}

/**
 * A duration in the given unit: "1,5 Tage", or in hours "0,7 Std." (one decimal under ten hours) and
 * "16 Std." (whole hours from ten up, which also keeps a value of a day or more readable, "36 Std.").
 */
export function formatDurationIn(days: number, unit: MiraDurationUnit, ctx: MiraDryingContext): string {
  if (unit === "days") {
    return formatDays(days, ctx);
  }
  const hours = days * 24;
  return hoursText(Math.abs(hours) < 10 ? ctx.formatDecimal(hours, 1) : ctx.formatNumber(Math.round(hours)), ctx);
}

/**
 * A single duration on its own, in its own unit ({@link durationUnit}). Only for a value that stands
 * alone; a figure with a headline formats every part with {@link formatDurationIn} in the headline's unit.
 */
export function formatDuration(days: number, ctx: MiraDryingContext): string {
  return formatDurationIn(days, durationUnit(days), ctx);
}

/** The neutral change of a duration in its figure's unit: "+1,2 Tage", or "+3,5 Std." for an hours figure. */
export function durationDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
  unit: MiraDurationUnit,
  ctx: MiraDryingContext
): MiraViewDelta | null {
  const factor = unit === "hours" ? 24 : 1;
  const scale = (value: number | null | undefined) => (value === null || value === undefined ? value : value * factor);
  const format = unit === "hours" ? (magnitude: string) => hoursText(magnitude, ctx) : (magnitude: string) => daysText(magnitude, ctx);
  return neutralDelta(scale(current), scale(previous), 1, format, ctx);
}

/**
 * The Verlauf / comparison / Markt formatter of a duration figure, in the unit its headline picked. An
 * hours formatter also names its axis step ({@link MiraValueFormatter.axisUnit}), so the chart puts its
 * ticks on whole hours rather than on tenths of a day.
 */
export function durationFormatter(unit: MiraDurationUnit, ctx: MiraDryingContext): MiraValueFormatter {
  const format: MiraValueFormatter = (value: number) => formatDurationIn(value, unit, ctx);
  if (unit === "hours") {
    format.axisUnit = MIRA_HOUR_IN_DAYS;
  }
  return format;
}

/**
 * The four duration texts of a tile, all in the one unit its headline picks: the value, the median
 * caption, the P15 / P85 line and the change against the comparison period.
 */
export function durationTileTexts(metric: MiraPublishedMetric, previous: MiraPublishedMetric | null, ctx: MiraDryingContext) {
  const unit = durationUnit(metric.value);
  const format = (value: number) => formatDurationIn(value, unit, ctx);
  return {
    value: format(metric.value ?? 0),
    median: medianMeasure(metric, format, ctx),
    spread: spreadLine(metric.p15, metric.p85, format, ctx),
    delta: durationDelta(metric.value, previous?.value, unit, ctx),
  };
}

function tile(
  id: string,
  icon: string,
  value: string,
  caption: string | null,
  delta: MiraViewDelta | null,
  ctx: MiraViewContext,
  spread: string | null = null
): MiraTile {
  return { id, icon, title: metricTitle(id, ctx), value, caption, delta, status: null, statusLabel: "", spread };
}

/**
 * The Trocknung KPI row: a tile for each figure the period publishes, up to eight.
 *
 * **Only a published figure gets a tile** (plan §5.7). One that waits on the company's data
 * (`INSUFFICIENT_DATA`) is listed in the unlock list instead, with what would unlock it; one withheld
 * by a decision is named on the "In Vorbereitung" line; one that cannot apply, or that we have not
 * measured, is not shown at all. Only a published figure whose period had nothing (`value: null`)
 * reads "Keine Daten im Zeitraum". Showing the others as empty tiles was the first version's mistake:
 * forecast versus actual read "no data" in a year where it was waiting on data (SanTec, the 12 months
 * before, 38 % covered against a 40 % floor).
 */
export function buildDryingTiles(
  period: MiraPublishedPeriod | null,
  comparison: MiraPublishedPeriod | null,
  ctx: MiraDryingContext
): MiraTile[] {
  const tiles: MiraTile[] = [];
  const published = (id: string) => period?.metrics[id]?.state === MiraAvailabilityState.AVAILABLE;
  const points = (magnitude: string) => ctx.t("mira.format.deltaPoints", [magnitude]);
  const squareMetres = (magnitude: string) => ctx.t("mira.drying.units.squareMetres", [magnitude]);
  const perWeek = (magnitude: string) => ctx.t("mira.drying.units.perWeek", [magnitude]);
  const perProject = (magnitude: string) => ctx.t("mira.drying.units.perProject", [magnitude]);

  const add = (id: string, icon: string, build: (metric: MiraPublishedMetric, previous: MiraPublishedMetric | null) => MiraTile) => {
    if (!published(id)) return;
    const metric = publishedMetric(period, id);
    // No tile without a value (Taimoor, 2026-10-02): what a company would unlock goes on its own page (D2).
    if (metric) tiles.push(build(metric, publishedMetric(comparison, id)));
  };

  add("MIRA-DRY-001", "mdi-water", (metric, previous) => {
    const family = headlineFamily(metric);
    const count = family ? metric.breakdown?.[`${family}Dryings`] ?? metric.basis ?? 0 : metric.basis ?? 0;
    const texts = durationTileTexts(metric, previous, ctx);
    return tile("MIRA-DRY-001", "mdi-water", texts.value,
      family
        ? ctx.t("mira.drying.captions.dryingTime", [texts.median, ctx.formatNumber(count), familyLabel(family, ctx)])
        : ctx.t("mira.drying.captions.dryingsWithoutFamily", [texts.median, ctx.formatNumber(count)]),
      texts.delta, ctx, texts.spread);
  });

  add("MIRA-DRY-002", "mdi-pause-circle-outline", (metric, previous) => {
    const current = metric.value ?? 0;
    const prior = previous?.value;
    const family = headlineFamily(metric);
    return tile("MIRA-DRY-002", "mdi-pause-circle-outline", formatPercent(current, ctx),
      family
        ? ctx.t("mira.drying.captions.pauseShare", [
          medianMeasure(metric, (value) => formatPercent(value, ctx), ctx),
          ctx.formatNumber(metric.basis ?? 0),
          familyLabel(family, ctx),
        ])
        : ctx.t("mira.drying.captions.dryingsWithoutFamily", [
          medianMeasure(metric, (value) => formatPercent(value, ctx), ctx),
          ctx.formatNumber(metric.basis ?? 0),
        ]),
      neutralDelta(current * 100, prior === null || prior === undefined ? null : prior * 100, 0, points, ctx, false), ctx,
      spreadLine(metric.p15, metric.p85, (value) => formatPercent(value, ctx), ctx));
  });

  // Per finished project (O1 / O2): the device period, HyDry and conventional projects apart. A project
  // with any HyDry device is a HyDry project (Hans, 2026-10-14).
  const projectDuration = (id: string, icon: string) => add(id, icon, (metric, previous) => {
    const texts = durationTileTexts(metric, previous, ctx);
    return tile(id, icon, texts.value,
      ctx.t("mira.drying.captions.projectDryingTime", [texts.median, ctx.formatNumber(metric.basis ?? 0)]),
      texts.delta, ctx, texts.spread);
  });
  projectDuration("MIRA-DRY-009", "mdi-water-outline");
  projectDuration("MIRA-DRY-010", "mdi-fan");

  add("MIRA-DRY-011", "mdi-pause-circle", (metric, previous) => {
    const current = metric.value ?? 0;
    const prior = previous?.value;
    return tile("MIRA-DRY-011", "mdi-pause-circle", formatPercent(current, ctx),
      ctx.t("mira.drying.captions.projectPauseShare", [
        medianMeasure(metric, (value) => formatPercent(value, ctx), ctx),
        ctx.formatNumber(metric.basis ?? 0),
      ]),
      neutralDelta(current * 100, prior === null || prior === undefined ? null : prior * 100, 0, points, ctx, false), ctx,
      spreadLine(metric.p15, metric.p85, (value) => formatPercent(value, ctx), ctx));
  });

  if (hasMeasurementsPerProject(period)) {
    // Zwischenmessungen je Projekt (Hans, #2610112 X5): over the dried projects with a set-up round.
    add(MIRA_MEASUREMENTS_PER_PROJECT, "mdi-clipboard-pulse-outline", (metric, previous) =>
      tile(MIRA_MEASUREMENTS_PER_PROJECT, "mdi-clipboard-pulse-outline", formatPerProject(metric.value ?? 0, ctx),
        ctx.t("mira.drying.captions.measurementsPerProject", [
          formatPercent(metric.breakdown?.shareWithoutIntermediate ?? 0, ctx),
          ctx.formatNumber(metric.basis ?? 0),
        ]),
        neutralDelta(metric.value, previous?.value, 2, perProject, ctx), ctx));
  } else {
    // A document from before Zwischenmessungen je Projekt: the per-week figure, as it was drawn then.
    add("MIRA-DRY-005", "mdi-clipboard-pulse-outline", (metric, previous) =>
      tile("MIRA-DRY-005", "mdi-clipboard-pulse-outline", formatPerWeek(metric.value ?? 0, ctx),
        ctx.t("mira.drying.captions.measurementFrequency", [
          formatPercent(metric.breakdown?.shareWithoutIntermediate ?? 0, ctx),
          ctx.formatNumber(metric.basis ?? 0),
        ]),
        neutralDelta(metric.value, previous?.value, 2, perWeek, ctx), ctx));
  }

  add("MIRA-DRY-007", "mdi-ruler-square", (metric, previous) =>
    tile("MIRA-DRY-007", "mdi-ruler-square", formatSquareMetres(metric.value ?? 0, ctx),
      ctx.t("mira.drying.captions.dryingArea", [
        medianMeasure(metric, (value) => formatSquareMetres(value, ctx), ctx),
        ctx.formatNumber(metric.basis ?? 0),
      ]),
      neutralDelta(metric.value, previous?.value, 1, squareMetres, ctx), ctx,
      spreadLine(metric.p15, metric.p85, (value) => formatSquareMetres(value, ctx), ctx)));

  add("MIRA-DRY-008", "mdi-calendar-clock", (metric, previous) => {
    const overrun = metric.value ?? 0;
    const unit = durationUnit(overrun);
    const sign = overrun > 0 ? "+" : overrun < 0 ? "−" : "";
    const magnitude = formatDurationIn(Math.abs(overrun), unit, ctx);
    return tile("MIRA-DRY-008", "mdi-calendar-clock", sign ? `${sign}${magnitude}` : magnitude,
      ctx.t("mira.drying.captions.forecast", [
        formatPercent(metric.breakdown?.shareOverForecast ?? 0, ctx),
        ctx.formatNumber(metric.basis ?? 0),
      ]),
      durationDelta(metric.value, previous?.value, unit, ctx), ctx);
  });

  return tiles;
}

/** DRY-001 per HyDry category: mean and median on one axis, every category the client dried with. */
export function buildDryingTimeRows(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraDotRow[] {
  const metric = publishedMetric(period, "MIRA-DRY-001");
  const breakdown = metric?.breakdown ?? {};
  // Every row in the DRY-001 tile's unit, so one card never mixes hours and days.
  const unit = durationUnit(metric?.value);

  return MIRA_FAMILIES.filter((family) => (breakdown[`${family}Dryings`] ?? 0) > 0).map((family) => ({
    key: family,
    label: familyLabel(family, ctx),
    color: familyColor(family, ctx.dark),
    // Under 10 dryings the row shows "zu wenige" instead of its marks (Track #2610072 S1).
    note: belowRowFloor(breakdown[`${family}Dryings`] ?? 0) ? ctx.t("mira.trend.tooFew") : null,
    mean: breakdown[`${family}MeanDays`] ?? 0,
    median: breakdown[`${family}MedianDays`] ?? 0,
    meanText: formatDurationIn(breakdown[`${family}MeanDays`] ?? 0, unit, ctx),
    medianText: formatDurationIn(breakdown[`${family}MedianDays`] ?? 0, unit, ctx),
    // The number alone: the column's header already says "Trocknungen", and repeating the word in every
    // row made the German text overflow its column.
    countText: ctx.formatNumber(breakdown[`${family}Dryings`] ?? 0),
    p15: breakdown[`${family}P15Days`] ?? null,
    p85: breakdown[`${family}P85Days`] ?? null,
  }));
}

/**
 * The formatter the Verlauf card uses for a Trocknung figure, so its points read in the same unit as
 * the tile: days (or hours, where the tile's headline for the chosen period is under one day), a share,
 * a rate or a count. Neutral, like every Trocknung figure.
 */
export function dryingTrendFormatter(metricId: string, ctx: MiraDryingContext, period: MiraPublishedPeriod | null = null): MiraValueFormatter {
  switch (metricId) {
    case "MIRA-DRY-001":
    case "MIRA-DRY-003":
    case "MIRA-DRY-008":
    case "MIRA-DRY-009":
    case "MIRA-DRY-010":
      return durationFormatter(durationUnit(publishedMetric(period, metricId)?.value), ctx);
    case "MIRA-DRY-002":
    case "MIRA-DRY-011":
      return (value) => formatPercent(value, ctx);
    case "MIRA-DRY-005":
      return (value) => formatPerWeek(value, ctx);
    case MIRA_MEASUREMENTS_PER_PROJECT:
      return (value) => formatPerProject(value, ctx);
    case "MIRA-DRY-007":
      return (value) => formatSquareMetres(value, ctx);
    default:
      return (value) => ctx.formatNumber(value);
  }
}

/** A round axis end at or above the largest value: 5, 10, 15, 20, 30, 40, 50, then tens. */
export function niceAxisMax(value: number): number {
  const steps = [5, 10, 15, 20, 30, 40, 50];
  const step = steps.find((candidate) => candidate >= value);
  return step ?? Math.ceil(value / 10) * 10;
}

/** The chains too short to count as a drying, stated under the dot plot rather than silently dropped. */
export function shortChainsNote(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): string | null {
  const count = publishedMetric(period, "MIRA-DRY-001")?.breakdown?.shortChains;
  return count ? ctx.t("mira.drying.shortChains", [ctx.formatNumber(count)]) : null;
}

/**
 * DRY-002 per HyDry category: its pause time as a share of its drying time, split into drying pauses
 * and power cuts. A Panel's drying pause is its cooldown, inactive time and Nachlauf (program 0) runs
 * since 2026-10-14 (Hans O4); before IRES Cloud published that part it had only power cuts. A part
 * with nothing in it is not drawn.
 */
export function buildPauseRows(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraSplitRow[] {
  const breakdown = publishedMetric(period, "MIRA-DRY-002")?.breakdown ?? {};
  const pauseColor = ctx.dark ? COLOR_MIRA_PAUSE_DRYING_DARK : COLOR_MIRA_PAUSE_DRYING;
  const powerCutColor = ctx.dark ? COLOR_MIRA_PAUSE_POWER_CUT_DARK : COLOR_MIRA_PAUSE_POWER_CUT;

  return MIRA_FAMILIES.filter((family) => (breakdown[`${family}Dryings`] ?? 0) > 0).map((family) => {
    const dryings = breakdown[`${family}Dryings`] ?? 0;
    const dryingsText = unitText("dryings", dryings, ctx);
    // Under 10 dryings: no bar and no share, only "zu wenige" and the dryings (Track #2610072 S1).
    if (belowRowFloor(dryings)) {
      return { key: family, label: familyLabel(family, ctx), parts: [], valueText: ctx.t("mira.trend.tooFew"), note: dryingsText };
    }
    return {
      key: family,
      label: familyLabel(family, ctx),
      parts: [
        { key: "dryingPause", value: breakdown[`${family}DryingPauseShare`] ?? 0, color: pauseColor },
        { key: "powerCut", value: breakdown[`${family}PowerFailShare`] ?? 0, color: powerCutColor },
      ].filter((part) => part.value > 0),
      valueText: formatPercent(breakdown[`${family}PauseShare`] ?? 0, ctx),
      // The dryings behind the row beside its share with a pause (Track #2610072 S1).
      note: `${ctx.t("mira.drying.shareWithPause", [formatPercent(breakdown[`${family}ShareWithPause`] ?? 0, ctx)])} · ${dryingsText}`,
    };
  });
}

/** The pause card's legend: what the two colours of a split mean. */
export function pauseLegend(ctx: MiraDryingContext): Array<{ label: string; color: string }> {
  return [
    { label: ctx.t("mira.drying.pause.dryingPause"), color: ctx.dark ? COLOR_MIRA_PAUSE_DRYING_DARK : COLOR_MIRA_PAUSE_DRYING },
    { label: ctx.t("mira.drying.pause.powerCut"), color: ctx.dark ? COLOR_MIRA_PAUSE_POWER_CUT_DARK : COLOR_MIRA_PAUSE_POWER_CUT },
  ];
}

/**
 * The measurement figure per group, as the figure itself defines them: HyDry devices, conventional devices
 * only, and no device booked. Its own published definition of measurement discipline, not a drying-time
 * comparison, so the no-side-by-side rule does not apply (plan, 2026-09-30). Zwischenmessungen je Projekt
 * (DRY-012, #2610112 X5) where the document carries it, each group per project; else DRY-005 per week.
 */
export function buildMeasurementRows(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraBar[] {
  const perProject = hasMeasurementsPerProject(period);
  const breakdown = publishedMetric(period, perProject ? MIRA_MEASUREMENTS_PER_PROJECT : "MIRA-DRY-005")?.breakdown ?? {};
  const countKey = (group: string) => (perProject ? `${group}Projects` : `${group}Dryings`);
  const rateKey = (group: string) => (perProject ? `${group}PerProject` : `${group}PerWeek`);
  const groups = ["hydry", "conventional", "noDevices"].filter((group) => (breakdown[countKey(group)] ?? 0) > 0);
  // A group under 10 cases shows "zu wenige" and no bar (Track #2610072 S1), so it does not set the scale.
  const shown = (group: string) => !belowRowFloor(breakdown[countKey(group)] ?? 0);
  const largest = Math.max(0, ...groups.filter(shown).map((group) => breakdown[rateKey(group)] ?? 0));

  return groups.map((group) => ({
    key: group,
    label: ctx.t(`mira.drying.measurementGroups.${group}`, [ctx.formatNumber(breakdown[countKey(group)] ?? 0)]),
    fraction: shown(group) && largest > 0 ? (breakdown[rateKey(group)] ?? 0) / largest : 0,
    valueText: shown(group) ? ctx.formatDecimal(breakdown[rateKey(group)] ?? 0, 2) : ctx.t("mira.trend.tooFew"),
    color: ctx.dark ? COLOR_MIRA_BAR_DARK : COLOR_MIRA_BAR,
    trackColor: ctx.dark ? COLOR_MIRA_BAR_TRACK_DARK : COLOR_MIRA_BAR_TRACK,
    deltaText: null,
    deltaDirection: "flat" as const,
    showDelta: false,
  }));
}

/** DRY-007: the mean area split into wall / ceiling and floor. */
export function buildAreaSegments(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraSegment[] {
  const metric = publishedMetric(period, "MIRA-DRY-007");
  const breakdown = metric?.breakdown;
  const total = (breakdown?.floorM2 ?? 0) + (breakdown?.wallCeilingM2 ?? 0);
  if (!breakdown || total === 0) {
    return [];
  }

  return [
    { key: "floor", value: breakdown.floorM2 ?? 0, color: ctx.dark ? COLOR_MIRA_AREA_FLOOR_DARK : COLOR_MIRA_AREA_FLOOR },
    { key: "wallCeiling", value: breakdown.wallCeilingM2 ?? 0, color: ctx.dark ? COLOR_MIRA_AREA_WALL_DARK : COLOR_MIRA_AREA_WALL },
  ].map((part) => ({
    key: part.key,
    label: ctx.t(`mira.drying.area.${part.key}`),
    value: part.value,
    valueText: formatSquareMetres(part.value, ctx),
    shareText: formatPercent(part.value / total, ctx),
    color: part.color,
  }));
}

export function areaFacts(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraFact[] {
  const metric = publishedMetric(period, "MIRA-DRY-007");
  if (!metric) return [];

  return [
    { label: ctx.t("mira.drying.facts.median"), value: formatSquareMetres(metric.median ?? 0, ctx) },
    { label: ctx.t("mira.drying.facts.withoutArea"), value: ctx.formatNumber(metric.breakdown?.withoutArea ?? 0) },
  ];
}

/**
 * DRY-008 as a meter: the share of dryings that ran longer than planned. Overall only: the published
 * HyDry / conventional split is deliberately not shown (see the header).
 */
export function forecastMeter(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraBar | null {
  const metric = publishedMetric(period, "MIRA-DRY-008");
  if (!metric) return null;

  const share = metric.breakdown?.shareOverForecast ?? 0;
  return {
    key: "overForecast",
    label: ctx.t("mira.drying.forecast.overForecast"),
    fraction: share,
    valueText: formatPercent(share, ctx),
    countText: typeof metric.basis === "number" ? casesText("MIRA-DRY-008", metric.basis, ctx) : null,
    color: ctx.dark ? COLOR_MIRA_BAR_DARK : COLOR_MIRA_BAR,
    trackColor: ctx.dark ? COLOR_MIRA_BAR_TRACK_DARK : COLOR_MIRA_BAR_TRACK,
    deltaText: null,
    deltaDirection: "flat",
    showDelta: false,
  };
}

export function forecastFacts(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraFact[] {
  const metric = publishedMetric(period, "MIRA-DRY-008");
  if (!metric) return [];
  const breakdown = metric.breakdown ?? {};
  // In the DRY-008 tile's unit, so the facts never read in hours beside a tile in days.
  const unit = durationUnit(metric.value);

  return [
    { label: ctx.t("mira.drying.facts.meanOverrun"), value: formatDurationIn(metric.value ?? 0, unit, ctx) },
    { label: ctx.t("mira.drying.facts.median"), value: formatDurationIn(metric.median ?? 0, unit, ctx) },
    { label: ctx.t("mira.drying.facts.fromDefault"), value: formatPercent(breakdown.shareFromDefault ?? 0, ctx) },
    { label: ctx.t("mira.drying.facts.noForecast"), value: ctx.formatNumber(breakdown.noForecast ?? 0) },
    { label: ctx.t("mira.drying.facts.noActual"), value: ctx.formatNumber(breakdown.noActual ?? 0) },
  ];
}

/** The section's figures withheld by a decision in the period, named so the gap is stated, not blank. */
export function inPreparation(period: MiraPublishedPeriod | null, ctx: MiraViewContext): string[] {
  return Object.entries(period?.metrics ?? {})
    .filter(([, metric]) => metric.state === MiraAvailabilityState.SUPPRESSED)
    .map(([id]) => metricTitle(id, ctx))
    .sort();
}
